// src/pages/partners/PartnerPriceList.tsx
//
// PartnerPriceList
// - Admin page for managing partner-specific product pricing.
// - Supports inline edits for discount rate (%) and visibility (VISIBLE/HIDDEN).
// - Uses a "draft vs applied" search model:
//   - Draft: UI input state (does not trigger fetch).
//   - Applied: drives data fetching and URL query params (shareable deep links).
//
// URL synchronization
// - URL query params always represent the *applied* filters + current page.
// - When URL changes (back/forward or pasted link), applied/draft/page are re-hydrated.
//
// Search rule (legacy behavior)
// - If productCode or productName length is exactly 1, Search is ignored.
//
// Editing policy
// - "Apply" persists a single row and refreshes the original snapshot for that row.
// - "Apply All" persists only dirty rows with a concurrency limit.
// - Dirty detection uses:
//   - originalMap (state) for immediate UI rendering.
//   - originalMapRef (ref) for handler-time checks to avoid stale closures.
//
// Base discount rate
// - Persists partner.baseDiscountRate via updatePartner(partnerId, partner).
// - "Apply" additionally updates only rows that are "unspecified":
//   - discountRate is 0 now AND was 0 in the snapshot (prevents overriding explicit edits).
//
// Table policy
// - Standard UI: StandardTableToolbar + StandardTableFrame + StandardPagination
// - Index (#) column is display-only. No navigation.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckCircle, Download, UploadFile } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Decimal } from 'decimal.js';
import { utcToZonedTime } from 'date-fns-tz';

import type { Partner, PartnerProductPriceResponse } from '@models/partner';
import type { GetProductsParams, ProductSaleType } from '@apis/product';
import { ProductSaleType as ProductSaleTypeEnum } from '@apis/product';
import type { ProductStatus } from '@models/product';
import { ProductStatus as ProductStatusEnum } from '@models/product';

import { usePartnerDetail } from '@queries/partners/usePartnerDetail';
import { usePartnerProductPrices } from '@queries/partners/usePartnerProductPrices';
import { usePartnerProductPriceCount } from '@queries/partners/usePartnerProductPriceCount';
import { useCountryList } from '@queries/countries/useCountryList';
import { useAllStockList } from '@queries/stocks/useAllStockList';

import {
  exportPartnerPricesExcel,
  updatePartner,
  updatePartnerProductPrice,
} from '@apis/partner';

import { filterSearchParams } from '@utils/search';
import { downloadBlob } from '@utils/file';
import { toUsdDisplay } from '@utils/money';
import {
  PRODUCT_SALE_TYPE_LABELS,
  PRODUCT_STATUS_LABELS,
  getProductStatusMeta,
} from '@utils/productLabels';
import { toDisplayPartnerId } from './util';

import { KOREAN_TIME_ZONE } from '@constants/timezone';

import { usePageHeader } from '@components/common/pageHeader.context';
import PartnerPriceListSearchPanel from '@components/partner/PartnerPriceListSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import {
  StandardTableToolbar,
  StandardTableFrame,
  StandardPagination,
  cellSx,
} from '@components/common/table';

type ProductSearchForm = GetProductsParams;

type Option<T> = {
  label: string;
  value: T;
};

const PAGE_SIZE = 50;
const APPLY_ALL_CONCURRENCY = 5;

const EMPTY_PARTNER: Partner = {
  name: '',
  partnerId: 0,
  managerUsername: '',
  managerPassword: '',
  managerEmail: '',
  managerName: '',
  address: '',
  balance: 0,
  baseDiscountRate: 0,
  remoteIps: [],
  callbackUrl: '',
  appId: '',
  appSecret: '',
  createdAt: '',
  manualOrderEnabled: false,
  status: 'ACTIVE',
};

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const sortByLabel = <T,>(a: Option<T>, b: Option<T>) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const isValidProductSaleType = (v: string | null): v is ProductSaleType => {
  if (!v) return false;
  const values = Object.values(
    (ProductSaleTypeEnum as unknown) as Record<string, string>,
  ) as string[];
  return values.includes(v);
};

const isValidProductStatus = (v: string | null): v is ProductStatus => {
  if (!v) return false;
  const values = Object.values(
    (ProductStatusEnum as unknown) as Record<string, string>,
  ) as string[];
  return values.includes(v);
};

/**
 * Accepts intermediate typing values for percentage fields:
 * - "", "-", "-." (while typing)
 * - -10, -10.5, -10.55, 10, 10.25
 * Up to 2 decimal places.
 */
const isValidPctTyping = (raw: string) => /^-?\d*(\.\d{0,2})?$/.test(raw);

const normalizePctText = (raw: string, opts?: { min?: number; max?: number }) => {
  const min = opts?.min ?? -100;
  const max = opts?.max ?? 100;

  // Treat empty / "-" / "-." as 0 on blur.
  if (raw === '' || raw === '-' || raw === '-.') return { text: '0.00', pct: 0 };

  const n = Number(raw);
  if (!Number.isFinite(n)) return { text: '0.00', pct: 0 };

  const clamped = clamp(n, min, max);
  const fixed = new Decimal(clamped).toFixed(2);
  return { text: fixed, pct: Number(fixed) };
};

const getProductStateChip = (status?: ProductStatus | string | null) => {
  return getProductStatusMeta(status);
};

/**
 * Concurrency-limited runner without additional dependencies.
 * Results are stored by original index so callers can summarize successes/failures deterministically.
 */
async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<{
  results: Array<
    | { item: T; index: number; ok: true; value: R }
    | { item: T; index: number; ok: false; error: unknown }
  >;
}> {
  const results: Array<
    | { item: T; index: number; ok: true; value: R }
    | { item: T; index: number; ok: false; error: unknown }
  > = [];

  let nextIndex = 0;

  const runners = Array.from({ length: Math.max(1, limit) }, async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const current = nextIndex++;
      if (current >= items.length) return;

      const item = items[current];
      try {
        const value = await worker(item, current);
        results[current] = { item, index: current, ok: true, value };
      } catch (error) {
        results[current] = { item, index: current, ok: false, error };
      }
    }
  });

  await Promise.all(runners);
  return { results };
}

/* -------------------------------------------------------------------------- */
/* URL <-> State                                                              */
/* -------------------------------------------------------------------------- */

const readSearchFromUrl = (sp: URLSearchParams): ProductSearchForm => {
  const saleTypeRaw = sp.get('productSaleType');
  const statusRaw = sp.get('productStatus');
  const countryIdRaw = sp.get('countryId');
  const stockIdRaw = sp.get('stockId');

  return {
    productCode: sp.get('productCode') || '',
    productName: sp.get('productName') || '',
    countryId: countryIdRaw ? Number(countryIdRaw) : undefined,
    stockId: stockIdRaw ? Number(stockIdRaw) : undefined,
    productSaleType: isValidProductSaleType(saleTypeRaw) ? saleTypeRaw : undefined,
    productStatus: isValidProductStatus(statusRaw) ? statusRaw : undefined,
  };
};

const cleanSearchForm = (form: ProductSearchForm): ProductSearchForm => {
  const code = normalizeText(form.productCode);
  const name = normalizeText(form.productName);

  return {
    ...form,
    productCode: code,
    productName: name,
    countryId: typeof form.countryId === 'number' ? form.countryId : undefined,
    stockId: typeof form.stockId === 'number' ? form.stockId : undefined,
    productSaleType: form.productSaleType || undefined,
    productStatus: form.productStatus || undefined,
  };
};

const searchFormsEqual = (a: ProductSearchForm, b: ProductSearchForm) => {
  const ac = cleanSearchForm(a);
  const bc = cleanSearchForm(b);

  return (
    (ac.productCode ?? '') === (bc.productCode ?? '') &&
    (ac.productName ?? '') === (bc.productName ?? '') &&
    (ac.countryId ?? null) === (bc.countryId ?? null) &&
    (ac.stockId ?? null) === (bc.stockId ?? null) &&
    (ac.productSaleType ?? null) === (bc.productSaleType ?? null) &&
    (ac.productStatus ?? null) === (bc.productStatus ?? null)
  );
};

const buildUrlParams = (form: ProductSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);
  const params: Record<string, string> = {};

  if (cleaned.productCode) params.productCode = cleaned.productCode;
  if (cleaned.productName) params.productName = cleaned.productName;

  if (typeof cleaned.countryId === 'number') params.countryId = String(cleaned.countryId);
  if (typeof cleaned.stockId === 'number') params.stockId = String(cleaned.stockId);

  if (cleaned.productSaleType) params.productSaleType = String(cleaned.productSaleType);
  if (cleaned.productStatus) params.productStatus = String(cleaned.productStatus);

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

const toSafeFileName = (raw: string) =>
  raw
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const PartnerPriceList = () => {
  const { partnerId } = useParams();
  const safePartnerId = Number(partnerId);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { setPageHeaderSlots } = usePageHeader();
  const systemAlert = useSystemAlert();

  /* ---------------- Partner identity (header source) ---------------- */

  const [partner, setPartner] = useState<Partner>(EMPTY_PARTNER);
  const { data: partnerDetailForm } = usePartnerDetail(safePartnerId);

  useEffect(() => {
    const loaded = partnerDetailForm?.data?.[0];
    if (!loaded) return;
    setPartner(loaded);
  }, [partnerDetailForm]);

  const partnerName = partner.name || 'Partner';
  const partnerDisplayId = toDisplayPartnerId(partner.partnerId);

  /* ---------------- Search state (draft vs applied) ---------------- */

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [appliedSearch, setAppliedSearch] = useState<ProductSearchForm>(() =>
    readSearchFromUrl(searchParams),
  );
  const [draftSearch, setDraftSearch] = useState<ProductSearchForm>(() => ({ ...appliedSearch }));

  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  /**
   * When the URL changes, treat it as the source of truth.
   * This enables shareable deep links and correct back/forward behavior.
   */
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setAppliedSearch(nextApplied);
    setPage(nextPage);

    // Keep draft in sync unless the draft already matches the new applied state.
    setDraftSearch((prev) => (searchFormsEqual(prev, nextApplied) ? prev : nextApplied));
  }, [searchParams]);

  /* ---------------- Queries (applied only) ---------------- */

  const { data: partnerProductPrices, isLoading: isLoadingPrices } = usePartnerProductPrices({
    partnerId: safePartnerId,
    page,
    pageSize: PAGE_SIZE,
    searchParam: appliedSearch,
  });

  const { data: partnerProductPriceCount, isLoading: isLoadingCount } = usePartnerProductPriceCount({
    partnerId: safePartnerId,
    searchParam: appliedSearch,
  });

  const totalCount = partnerProductPriceCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isTableLoading = Boolean(isLoadingPrices || isLoadingCount);

  /* ---------------- Local editable list + original snapshot ---------------- */

  const [prices, setPrices] = useState<PartnerProductPriceResponse[]>([]);
  const [originalMap, setOriginalMap] = useState<Record<number, PartnerProductPriceResponse>>({});

  /**
   * A ref is used in async handlers to avoid stale closures and dependency churn.
   * State remains the UI source of truth for rendering.
   */
  const originalMapRef = useRef(originalMap);
  useEffect(() => {
    originalMapRef.current = originalMap;
  }, [originalMap]);

  useEffect(() => {
    const list = partnerProductPrices?.data || [];
    setPrices(list);

    // Refresh snapshot whenever a new page is loaded.
    setOriginalMap(() => {
      const next: Record<number, PartnerProductPriceResponse> = {};
      for (const p of list) next[p.productId] = p;

      // Keep the ref aligned without an extra render.
      originalMapRef.current = next;
      return next;
    });
  }, [partnerProductPrices]);

  /* ---------------- Autocomplete options ---------------- */

  const { data: countriesData } = useCountryList();
  const { data: stocksData } = useAllStockList();

  const countryOptions: Option<number>[] = useMemo(() => {
    const raw = countriesData?.data ?? [];
    return raw
      .map((c) => ({ label: c.englishName, value: c.countryId as number }))
      .sort(sortByLabel);
  }, [countriesData]);

  const stockOptions: Option<number>[] = useMemo(() => {
    const raw = stocksData?.data ?? [];
    return raw.map((s) => ({ label: s.stockCode, value: s.stockId as number })).sort(sortByLabel);
  }, [stocksData]);

  const saleTypeOptions: Option<ProductSaleType>[] = useMemo(() => {
    const values = Object.values(
      (ProductSaleTypeEnum as unknown) as Record<string, string>,
    ) as ProductSaleType[];
    return values
      .map((v) => ({ value: v, label: PRODUCT_SALE_TYPE_LABELS?.[v] ?? String(v) }))
      .sort(sortByLabel);
  }, []);

  const statusOptions: Option<ProductStatus>[] = useMemo(() => {
    const values = Object.values(
      (ProductStatusEnum as unknown) as Record<string, string>,
    ) as ProductStatus[];
    return values
      .map((v) => ({ value: v, label: PRODUCT_STATUS_LABELS?.[v] ?? String(v) }))
      .sort(sortByLabel);
  }, []);

  const selectedCountry = useMemo(() => {
    if (!draftSearch.countryId) return null;
    return countryOptions.find((o) => o.value === draftSearch.countryId) ?? null;
  }, [countryOptions, draftSearch.countryId]);

  const selectedStock = useMemo(() => {
    if (!draftSearch.stockId) return null;
    return stockOptions.find((o) => o.value === draftSearch.stockId) ?? null;
  }, [stockOptions, draftSearch.stockId]);

  const selectedSaleType = useMemo(() => {
    if (!draftSearch.productSaleType) return null;
    return saleTypeOptions.find((o) => o.value === draftSearch.productSaleType) ?? null;
  }, [saleTypeOptions, draftSearch.productSaleType]);

  const selectedStatus = useMemo(() => {
    if (!draftSearch.productStatus) return null;
    return statusOptions.find((o) => o.value === draftSearch.productStatus) ?? null;
  }, [statusOptions, draftSearch.productStatus]);

  /* ---------------- Draft -> Applied ---------------- */

  const applyDraftAsApplied = useCallback(
    (nextDraft?: ProductSearchForm) => {
      const next = cleanSearchForm(nextDraft ?? draftSearch);
      setAppliedSearch(next);

      // Filter changes reset pagination.
      setPage(1);
      setSearchParams(buildUrlParams(next, 1));
    },
    [draftSearch, setSearchParams],
  );

  /**
   * SearchPanel input setter.
   * - Text fields remain controlled by using empty string.
   * - Select fields use undefined so they can be omitted from the URL.
   */
  const setValue: SetSearchValueFn<ProductSearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next = { ...prev } as Record<string, unknown>;

      if (key === 'productCode' || key === 'productName') {
        next[key] = (value ?? '') as string;
      } else {
        next[key] = (value ?? undefined) as unknown;
      }

      return next as ProductSearchForm;
    });
  }, []);

  const handleSearch = useCallback(() => {
    const code = (draftSearch.productCode ?? '').trim();
    const name = (draftSearch.productName ?? '').trim();

    // Legacy UX: ignore exactly 1-character searches.
    if (code.length === 1 || name.length === 1) return;

    applyDraftAsApplied();
  }, [applyDraftAsApplied, draftSearch.productCode, draftSearch.productName]);

  const handleReset = useCallback(() => {
    const cleared: ProductSearchForm = {
      productCode: '',
      productName: '',
      countryId: undefined,
      stockId: undefined,
      productSaleType: undefined,
      productStatus: undefined,
    };

    setDraftSearch(cleared);
    applyDraftAsApplied(cleared);
  }, [applyDraftAsApplied]);

  /* ---------------- Pagination ---------------- */

  const handleChangePage = (next: number) => {
    const safe = clamp(next, 1, totalPages);
    setPage(safe);
    setSearchParams(buildUrlParams(appliedSearch, safe));
  };

  /* ---------------- Base discount rate (partner-level) ---------------- */

  const [baseDiscountRatePct, setBaseDiscountRatePct] = useState<string>('0.00');

  useEffect(() => {
    const pct = (partner.baseDiscountRate ?? 0) * 100;
    setBaseDiscountRatePct(new Decimal(pct).toFixed(2));
  }, [partner.baseDiscountRate]);

  const [baseApplying, setBaseApplying] = useState(false);

  /**
   * Separate text inputs per row to support intermediate typing states.
   * Key: productId, Value: percentage string (e.g., "10.00")
   */
  const [discountInputs, setDiscountInputs] = useState<Record<number, string>>({});

  useEffect(() => {
    setDiscountInputs((prev) => {
      const next = { ...prev };
      for (const p of prices) {
        if (typeof next[p.productId] === 'undefined') {
          next[p.productId] = new Decimal((p.discountRate ?? 0) * 100).toFixed(2);
        }
      }
      return next;
    });
  }, [prices]);

  const applyBaseDiscountRate = useCallback(async () => {
    if (baseApplying) return;

    try {
      setBaseApplying(true);

      // Persist the partner-level base discount.
      await updatePartner(safePartnerId, partner);

      /**
       * Apply base to rows that are "unspecified":
       * - discountRate is 0 now AND was 0 in the snapshot.
       * This prevents overriding values explicitly configured by an operator.
       */
      const base = partner.baseDiscountRate ?? 0;
      const affected: number[] = [];

      setPrices((prev) =>
        prev.map((p) => {
          const o = originalMapRef.current[p.productId];
          const isUnspecifiedNow = (p.discountRate ?? 0) === 0;
          const wasUnspecifiedOriginally = (o?.discountRate ?? 0) === 0;

          if (isUnspecifiedNow && wasUnspecifiedOriginally) {
            affected.push(p.productId);
            return { ...p, discountRate: base };
          }
          return p;
        }),
      );

      if (affected.length > 0) {
        setDiscountInputs((prev) => {
          const next = { ...prev };
          for (const pid of affected) next[pid] = new Decimal(base * 100).toFixed(2);
          return next;
        });
      }

      systemAlert.success('Base discount rate applied.');
    } catch {
      systemAlert.error('Failed to apply base discount rate.');
    } finally {
      setBaseApplying(false);
    }
  }, [baseApplying, partner, safePartnerId, systemAlert]);

  /* ---------------- Export ---------------- */

  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    try {
      setExporting(true);

      // Use KST for filenames to match operator expectations.
      const kstDate = utcToZonedTime(new Date(), KOREAN_TIME_ZONE);

      const res = await exportPartnerPricesExcel(safePartnerId, appliedSearch);

      const safeName = toSafeFileName(partner.name || 'partner');
      const ts = kstDate.toISOString().replace(/[:]/g, '-');
      const fileName = `partner-${safeName}-prices-${ts}.xlsx`;

      downloadBlob(res, fileName);
      systemAlert.success('Export started.');
    } catch {
      systemAlert.error('Export failed.');
    } finally {
      setExporting(false);
    }
  }, [appliedSearch, isExporting, partner.name, safePartnerId, systemAlert]);

  /* ---------------- Upload navigation ---------------- */

  const handleUpload = useCallback(() => {
    navigate(`/partners/${safePartnerId}/prices/upload`);
  }, [navigate, safePartnerId]);

  /* ---------------- PageHeader slots ---------------- */

  useEffect(() => {
    setPageHeaderSlots({
      leading: {
        title: partnerName,
        description: `Partner ID: ${partnerDisplayId}`,
      },
      actions: [
        {
          key: 'partner-prices-download',
          label: 'Download',
          startIcon: <Download />,
          variant: 'outlined',
          onClick: handleExport,
          disabled: isExporting,
        },
        {
          key: 'partner-prices-upload',
          label: 'Upload',
          startIcon: <UploadFile />,
          variant: 'outlined',
          onClick: handleUpload,
        },
      ],
    });
  }, [handleExport, handleUpload, isExporting, partnerDisplayId, partnerName, setPageHeaderSlots]);

  /* ---------------- Row state updates ---------------- */

  const setDiscountInput = (productId: number, value: string) => {
    setDiscountInputs((prev) => ({ ...prev, [productId]: value }));
  };

  const updateDiscountRate = (productId: number, discountRate: number) => {
    setPrices((prev) => prev.map((p) => (p.productId === productId ? { ...p, discountRate } : p)));
  };

  const updateVisibility = (productId: number, status: 'VISIBLE' | 'HIDDEN') => {
    setPrices((prev) => prev.map((p) => (p.productId === productId ? { ...p, status } : p)));
  };

  /* ---------------- Dirty detection ---------------- */

  // Handler-time dirty check (ref-based).
  const isRowDirty = useCallback((p: PartnerProductPriceResponse) => {
    const o = originalMapRef.current[p.productId];
    if (!o) return false;
    return p.discountRate !== o.discountRate || p.status !== o.status;
  }, []);

  // UI-time dirty checks (state-based).
  const isRowDirtyByMap = useCallback(
    (p: PartnerProductPriceResponse, map: Record<number, PartnerProductPriceResponse>) => {
      const o = map[p.productId];
      if (!o) return false;
      return p.discountRate !== o.discountRate || p.status !== o.status;
    },
    [],
  );

  const isDiscountDirtyByMap = useCallback(
    (p: PartnerProductPriceResponse, map: Record<number, PartnerProductPriceResponse>) => {
      const o = map[p.productId];
      if (!o) return false;
      return p.discountRate !== o.discountRate;
    },
    [],
  );

  const dirtyRows = useMemo(
    () => prices.filter((p) => isRowDirtyByMap(p, originalMap)),
    [prices, originalMap, isRowDirtyByMap],
  );

  /* ---------------- Row apply ---------------- */

  const [rowApplying, setRowApplying] = useState<Record<number, boolean>>({});

  const savePrice = useCallback(
    async (productId: number) => {
      const price = prices.find((p) => p.productId === productId);
      if (!price) return;

      if (!isRowDirty(price)) return;
      if (rowApplying[productId]) return;

      try {
        setRowApplying((prev) => ({ ...prev, [productId]: true }));
        await updatePartnerProductPrice(price);

        // Refresh snapshot for this row after persistence (clears dirty state).
        setOriginalMap((prev) => {
          const next = { ...prev, [productId]: price };
          originalMapRef.current = next;
          return next;
        });

        systemAlert.success('Applied successfully.');
      } catch {
        systemAlert.error('Failed to apply. Please try again.');
      } finally {
        setRowApplying((prev) => ({ ...prev, [productId]: false }));
      }
    },
    [isRowDirty, prices, rowApplying, systemAlert],
  );

  /* ---------------- Apply All (dirty only) ---------------- */

  const [applyAllLoading, setApplyAllLoading] = useState(false);
  const [applyAllProgress, setApplyAllProgress] = useState<{ done: number; total: number } | null>(
    null,
  );

  const applyAllDirty = useCallback(async () => {
    if (applyAllLoading) return;

    const targets = prices.filter(isRowDirty);
    if (targets.length === 0) return;

    try {
      setApplyAllLoading(true);
      setApplyAllProgress({ done: 0, total: targets.length });

      // Disable per-row Apply buttons while batch applying.
      setRowApplying((prev) => {
        const next = { ...prev };
        for (const t of targets) next[t.productId] = true;
        return next;
      });

      const { results } = await runWithConcurrency(
        targets,
        APPLY_ALL_CONCURRENCY,
        async (row) => {
          await updatePartnerProductPrice(row);

          // Update snapshot as each row completes so the UI reflects applied rows immediately.
          setOriginalMap((prev) => {
            const next = { ...prev, [row.productId]: row };
            originalMapRef.current = next;
            return next;
          });

          setApplyAllProgress((p) => (p ? { ...p, done: Math.min(p.total, p.done + 1) } : p));
          return true;
        },
      );

      const okCount = results.filter((r) => r?.ok).length;
      const failCount = results.length - okCount;

      if (failCount === 0) systemAlert.success(`Applied ${okCount} item(s).`);
      else systemAlert.info(`Applied ${okCount} item(s). Failed ${failCount}.`);
    } catch {
      systemAlert.error('Apply All failed. Please try again.');
    } finally {
      setRowApplying({});
      setApplyAllLoading(false);
      setApplyAllProgress(null);
    }
  }, [applyAllLoading, isRowDirty, prices, systemAlert]);

  /* ---------------- Helper text (1-character search policy) ---------------- */

  const codeTrim = normalizeText(draftSearch.productCode);
  const nameTrim = normalizeText(draftSearch.productName);

  const codeHelper = codeTrim.length === 1 ? 'Enter at least 2 characters to search.' : ' ';
  const nameHelper = nameTrim.length === 1 ? 'Enter at least 2 characters to search.' : ' ';

  // StandardTableFrame uses columnsCount for empty/loading spans.
  // (# + Product + OrderType + StockCode + Visibility + OriginalPrice + DiscountRate + SupplyPrice + ProductState + Action)
  const columnsCount = 10;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* Base discount rate */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ minWidth: 0, flexWrap: 'wrap' }}
          >
            <TextField
              label="Base Discount Rate"
              size="small"
              value={baseDiscountRatePct}
              onChange={(e) => {
                const raw = e.target.value;

                // Allow intermediate typing states.
                if (raw === '' || raw === '-' || raw === '-.') {
                  setBaseDiscountRatePct(raw);
                  return;
                }
                if (!isValidPctTyping(raw)) return;

                const n = Number(raw);
                if (!Number.isFinite(n)) return;

                setBaseDiscountRatePct(raw);
                setPartner((prev) => ({ ...prev, baseDiscountRate: n / 100 }));
              }}
              onBlur={() => {
                const { text, pct } = normalizePctText(baseDiscountRatePct, { min: -100, max: 100 });
                setBaseDiscountRatePct(text);
                setPartner((prev) => ({ ...prev, baseDiscountRate: pct / 100 }));
              }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 260 } }}
            />

            <Button
              variant="contained"
              onClick={applyBaseDiscountRate}
              disabled={baseApplying}
              startIcon={
                baseApplying ? <CircularProgress size={16} /> : <CheckCircle fontSize="small" />
              }
              sx={{ whiteSpace: 'nowrap' }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Divider />

      {/* Filters */}
      <PartnerPriceListSearchPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        draftSearch={draftSearch}
        setValue={setValue}
        countryOptions={countryOptions}
        stockOptions={stockOptions}
        saleTypeOptions={saleTypeOptions}
        statusOptions={statusOptions}
        selectedCountry={selectedCountry}
        selectedStock={selectedStock}
        selectedSaleType={selectedSaleType}
        selectedStatus={selectedStatus}
        codeHelperText={codeHelper}
        nameHelperText={nameHelper}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {/* Standard toolbar (Total + Page Jump) */}
      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {/* Table */}
      <Box mt={1}>
        <StandardTableFrame<PartnerProductPriceResponse>
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell sx={{ minWidth: 260 }}>Product</TableCell>
              <TableCell align="center">Order Type</TableCell>
              <TableCell>Stock Code</TableCell>
              <TableCell align="center">Visibility</TableCell>
              <TableCell align="right">Original Price</TableCell>
              <TableCell align="right">Discount Rate</TableCell>
              <TableCell align="right">Supply Price</TableCell>
              <TableCell align="center">Product State</TableCell>

              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyAllDirty}
                  disabled={applyAllLoading || dirtyRows.length === 0}
                  startIcon={
                    applyAllLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CheckCircle fontSize="small" />
                    )
                  }
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Apply All{dirtyRows.length > 0 ? ` (${dirtyRows.length})` : ''}
                </Button>

                {applyAllProgress ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {applyAllProgress.done}/{applyAllProgress.total}
                  </Typography>
                ) : null}
              </TableCell>
            </TableRow>
          }
          rows={prices}
          getRowKey={(row) => String(row.productId)}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No products found."
          minWidth={1200}
          getRowStatus={(row) => {
            const status = row.productStatus as ProductStatus;

            if (status === ProductStatusEnum.DISCONTINUED) return 'inactive';
            if (status === ProductStatusEnum.OUT_OF_STOCK) return 'warning';
            return 'normal';
          }}
          renderRow={(price, index) => {
            const applying = Boolean(rowApplying[price.productId]);

            const discountText =
              discountInputs[price.productId] ??
              new Decimal(((price.discountRate ?? 0) * 100) || 0).toFixed(2);

            const effectiveDiscount = price.discountRate ?? 0;
            const supplyPrice = price.originalPrice * (1 - effectiveDiscount);

            const saleTypeLabel =
              PRODUCT_SALE_TYPE_LABELS?.[price.productSaleType as ProductSaleType] ??
              String(price.productSaleType);

            const rowDirty = isRowDirtyByMap(price, originalMap);
            const discountDirty = isDiscountDirtyByMap(price, originalMap);

            const showCustomBadge = discountDirty;
            const showBaseBadge =
              !discountDirty &&
              (price.discountRate ?? 0) === (partner.baseDiscountRate ?? 0) &&
              (partner.baseDiscountRate ?? 0) !== 0;

            return (
              <>
                {/* Index is display-only (no navigation) */}
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell sx={{ minWidth: 260 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    {price.productCode}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ display: 'block', lineHeight: 1.25, wordBreak: 'break-word' }}
                  >
                    {price.productName}
                  </Typography>
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {saleTypeLabel}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{price.stockCode}</TableCell>

                <TableCell align="center">
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={price.status === 'VISIBLE' ? 'VISIBLE' : 'HIDDEN'}
                    onChange={(_e, next) => {
                      if (!next) return;
                      updateVisibility(price.productId, next);
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1.25,
                        whiteSpace: 'nowrap',
                        borderColor: 'divider',
                      },
                    }}
                    disabled={applyAllLoading}
                  >
                    <ToggleButton
                      value="VISIBLE"
                      sx={(theme) => ({
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.success.main, 0.18),
                          borderColor: alpha(theme.palette.success.main, 0.6),
                          color: theme.palette.success.dark,
                          fontWeight: 700,
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: alpha(theme.palette.success.main, 0.24),
                        },
                      })}
                    >
                      Visible
                    </ToggleButton>

                    <ToggleButton
                      value="HIDDEN"
                      sx={(theme) => ({
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.grey[500], 0.18),
                          borderColor: alpha(theme.palette.grey[500], 0.5),
                          fontWeight: 700,
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: alpha(theme.palette.grey[500], 0.22),
                        },
                      })}
                    >
                      Hidden
                    </ToggleButton>
                  </ToggleButtonGroup>
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {toUsdDisplay(price.originalPrice)}
                </TableCell>

                <TableCell align="right">
                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                    {showCustomBadge ? (
                      <Typography
                        variant="caption"
                        sx={(theme) => ({
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.primary.main, 0.18),
                          color: theme.palette.primary.dark,
                          fontWeight: 800,
                          whiteSpace: 'nowrap',
                        })}
                      >
                        Custom
                      </Typography>
                    ) : showBaseBadge ? (
                      <Typography
                        variant="caption"
                        sx={(theme) => ({
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.grey[500], 0.18),
                          color: theme.palette.text.primary,
                          fontWeight: 800,
                          whiteSpace: 'nowrap',
                        })}
                      >
                        Base
                      </Typography>
                    ) : null}

                    <TextField
                      size="small"
                      value={discountText}
                      onChange={(e) => {
                        const raw = e.target.value;

                        if (raw === '' || raw === '-' || raw === '-.') {
                          setDiscountInput(price.productId, raw);
                          return;
                        }
                        if (!isValidPctTyping(raw)) return;

                        const n = Number(raw);
                        if (!Number.isFinite(n)) return;

                        setDiscountInput(price.productId, raw);
                        updateDiscountRate(price.productId, n / 100);
                      }}
                      onBlur={() => {
                        const { text, pct } = normalizePctText(discountText, { min: -100, max: 100 });
                        setDiscountInput(price.productId, text);
                        updateDiscountRate(price.productId, pct / 100);
                      }}
                      inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ width: 140 }}
                      disabled={applyAllLoading}
                    />
                  </Stack>
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {toUsdDisplay(supplyPrice)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {(() => {
                    const chip = getProductStateChip(price.productStatus as ProductStatus);
                    return (
                      <Chip
                        label={chip.label}
                        color={chip.chip.color}
                        size="small"
                        variant={chip.chip.variant}
                      />
                    );
                  })()}
                </TableCell>

                <TableCell align="center">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      applying ? <CircularProgress size={16} /> : <CheckCircle fontSize="small" />
                    }
                    onClick={() => savePrice(price.productId)}
                    disabled={!rowDirty || applying || applyAllLoading}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Apply
                  </Button>
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      {/* Bottom pagination */}
      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default PartnerPriceList;
